/**
 * Lightweight browser-locale–aware translation utility.
 *
 * Detects the user's preferred language from `navigator.language` and maps it
 * to one of the supported locales.  Falls back to English (`en`): the chrome
 * strings only exist in de/en, and a French or Polish site must not show its
 * validation / slotting / registration text in German (audit 2026-09-23, 4e).
 *
 * Usage:
 *   import { t } from "../locale";
 *   t("fieldRequired")            // "Dieses Feld ist erforderlich."
 *   t("minChars", "5")            // "Mindestens 5 Zeichen erforderlich."
 */

/* ------------------------------------------------------------------ */
/*  Supported locales                                                  */
/* ------------------------------------------------------------------ */

export type Locale = "de" | "en";

const SUPPORTED: Locale[] = ["de", "en"];
const DEFAULT_LOCALE: Locale = "en";

/**
 * Collapse any BCP-47 tag (e.g. "de-DE", "en-GB", "fr") to the nearest
 * supported UI-string table. The chrome strings (form validation, slotting, …)
 * only exist in de/en; content text is resolved separately per-language.
 */
export function resolveUiLocale(tag: string | null | undefined): Locale {
  const t = (tag ?? "").toLowerCase();
  for (const locale of SUPPORTED) {
    if (t === locale || t.startsWith(locale + "-")) return locale;
  }
  return DEFAULT_LOCALE;
}

function detectLocale(): Locale {
  if (typeof navigator === "undefined") return DEFAULT_LOCALE;
  const raw = navigator.language ?? (navigator.languages?.[0] as string | undefined) ?? "";
  return resolveUiLocale(raw);
}

let currentLocale: Locale = detectLocale();

export function getLocale(): Locale {
  return currentLocale;
}

/** Accepts a full BCP-47 tag or a bare Locale; collapses to the UI table. */
export function setLocale(locale: string): void {
  currentLocale = resolveUiLocale(locale);
}

/* ------------------------------------------------------------------ */
/*  Translation keys                                                   */
/* ------------------------------------------------------------------ */

export type TranslationKey =
  // form validation
  | "fieldRequired"
  | "emailRequired"
  | "dateRequired"
  | "selectRequired"
  | "statusRequired"
  | "invalidEmail"
  | "invalidUrl"
  | "invalidInteger"
  | "invalidNumber"
  | "minChars"
  | "maxChars"
  | "minValue"
  | "maxValue"
  | "minOptions"
  | "maxOptions"
  | "dateAfter"
  | "dateBefore"
  | "checkboxRequired"
  // IMAGE additional-field upload widget
  | "imageSelect"
  | "imageReplace"
  | "imageRemove"
  | "imageStored"
  | "imageUploading"
  | "imageTooBig"
  | "imageWrongType"
  | "imageUploadFailed"
  // form submission
  | "submitSuccess"
  | "submitFailed"
  // labels
  | "status"
  | "yes"
  | "no"
  // overview / verification
  | "verificationPage"
  | "checkSubmittedData"
  | "fields"
  | "companions"
  | "companionLabel"
  | "addCompanion"
  | "checkboxes"
  | "overviewBookingLabel"
  // registration
  | "registrationSuccessful"
  | "registrationSubmitted"
  | "pendingApprovalSubtitle"
  | "close"
  // misc
  | "update"
  | "noChanges"
  | "logoutFailed"
  // session (audit 2026-09-23, 4a)
  | "sessionExpired"
  | "slottingLoginRequired"
  // worker errorCode / status table (audit 2026-09-23, 4f) — 403 bodies are
  // operator text and are NOT keyed here
  | "errorDuplicateGuest"
  | "errorInvalidCredentials"
  | "errorTooManyAttempts"
  | "errorServiceUnavailable"
  | "submitValidationFailed"
  | "errorInvalidFieldType"
  // slotting - booking
  | "slottingSelectService"
  | "slottingSelectDate"
  | "slottingAvailableTimes"
  | "slottingNoAvailableDates"
  | "slottingNoAvailableTimes"
  | "slottingYourId"
  | "slottingEnterGuestId"
  | "slottingAddCompanions"
  | "slottingHideCompanions"
  | "slottingCompanionId"
  | "slottingAddCompanion"
  | "slottingRemove"
  | "slottingSubmitRequest"
  | "slottingBookAppointment"
  | "slottingSubmitting"
  | "slottingRequestSubmitted"
  | "slottingRequestSubmittedDesc"
  | "slottingBookingUnavailable"
  // slotting - appointment
  | "slottingYourAppointment"
  | "slottingService"
  | "slottingLocation"
  | "slottingDateTime"
  | "slottingOffering"
  | "slottingDownloadCalendar"
  | "slottingCancelledMessage"
  | "slottingStatusMessage"
  | "slottingInProgress"
  | "slottingTentative"
  | "slottingConfirmed"
  | "slottingAppointmentStatus"
  | "slottingCancelButton"
  | "slottingCancelConfirm"
  | "slottingCancelling"
  | "slottingYesCancel"
  | "slottingKeepAppointment"
  | "slottingCancelledSuccess"
  | "slottingCancellationFailed"
  | "slottingDeadlinePassed"
  | "slottingAppointmentNotFound"
  | "slottingRescheduled"
  | "slottingViewNewAppointment"
  // slotting - waitlist
  | "slottingWaitlistStatus"
  | "slottingWaitlistNotFound"
  | "slottingWaitlistPosition"
  | "slottingWaitlistPromoted"
  | "slottingViewAppointment"
  | "slottingLeaveWaitlist"
  | "slottingLeaving"
  | "slottingLeftWaitlist"
  | "slottingWaitlistInactive"
  | "slottingWaitlistStatusWaiting"
  | "slottingWaitlistStatusPromoted"
  | "slottingWaitlistStatusExpired"
  | "slottingWaitlistStatusCancelled"
  // slotting - spots
  | "slottingSpotsLeft"
  // slotting - availability
  | "slottingAvailable"
  | "slottingFull"
  | "slottingNone"
  // slotting - request-based
  | "slottingSelectTreatments"
  | "slottingYourAvailability"
  | "slottingAddWindow"
  | "slottingDateLabel"
  | "slottingStartTime"
  | "slottingEndTime"
  | "slottingTreatmentsSelected"
  | "slottingWindowsAdded"
  | "slottingRequestSummary"
  | "slottingNoRequestConfigured"
  // companion modal
  | "companionEdit"
  | "companionSave"
  | "companionCancel"
  | "companionNoName"
  // booking modal
  | "bookingEdit"
  | "bookingBook"
  | "bookingNoAppointment"
  | "bookingConfirm"
  | "bookingAccessDenied"
  | "bookingTreatmentsUnavailable"
  // booking validation
  | "bookingWindowDateRequired"
  | "bookingWindowEndBeforeStart"
  | "bookingWindowInPast"
  | "bookingWindowOutsideRange"
  | "bookingTreatmentSelectionRequired"
  | "bookingSelectionRequired"
  | "bookingSubmissionFailed"
  | "bookingSelectDate"
  | "bookingSelectTime"
  | "bookingDetails"
  | "bookingSelectTreatmentRequired"
  | "bookingSelectDateRequired"
  | "bookingSelectSlotRequired"
  // status badges
  | "slottingCancelled"
  | "slottingCheckedIn"
  // error fallbacks
  | "slottingFailedToLoad"
  | "slottingFailedToLeaveWaitlist"
  | "slottingBookingFailed"
  | "slottingNoAvailabilityData"
  // registration/login rules
  | "registrationClosed"
  | "loginClosed"
  | "waitlistTitle"
  | "waitlistSubtitle"
  | "waitlistPosition"
  // capacity refusals on submit (worker 403 "quotaFull" /
  // "companionLimitReached"). Neutral by decision: the organiser's internal
  // segment name never reaches the guest.
  | "registrationSegmentFull"
  | "companionLimitReached"
  // option capacity (worker 409 errorCode "optionCapacityFull", PB-N2-2)
  | "optionCapacityFull"
  | "optionCapacityFullGeneric"
  // session-selection programme step (P4; worker 409 errorCode "sessionFull")
  | "sessionStepLabel"
  | "sessionSeatsLeft"
  | "sessionSoldOut"
  | "sessionSelectedCount"
  | "sessionMinHint"
  | "sessionMaxHint"
  | "sessionMinError"
  | "sessionMaxError"
  | "sessionSoldOutSelected"
  | "sessionFullError"
  | "sessionsUnavailable"
  | "sessionsEmpty"
  | "sessionShowMore"
  | "sessionShowLess"
  // OFFERING slot booking inside the programme step (W3 + 1:n pivot;
  // worker 409 "offeringSlotConflict" / 400 "offeringNotDirectlyBookable"
  // / 409 "offeringMultipleAppointments")
  | "offeringChooseSlot"
  | "offeringChangeSlot"
  | "offeringHideSlots"
  | "offeringOnRequest"
  | "offeringCurrentBooking"
  | "offeringRemoveSelection"
  | "offeringSlotsUnavailable"
  | "offeringRetry"
  | "offeringConflictError"
  | "offeringNotBookableError"
  | "offeringChooseTreatment"
  | "offeringPickSlotHint"
  | "offeringMultipleAppointments"
  | "offeringMultipleAppointmentsError"
  | "offeringNoTypes"
  // (consent banner chrome moved to wbChrome — 57 languages, see consent/*)
  // additionalField validation (backend mirrors in AdditionalFieldConstraints.java)
  | "errors.additionalFieldsOverCap"
  | "errors.invalidAdditionalFieldKey"
  | "errors.additionalFieldKeyCollision"
  | "errors.reservedAdditionalFieldKey"
  | "errors.invalidAdditionalFieldConstraints"
  | "errors.additionalFieldsValidationFailed"
  | "errors.unknownAdditionalFieldKey"
  | "errors.invalidAdditionalFieldValueType"
  | "errors.additionalFieldValueOutOfRange"
  | "errors.invalidAdditionalFieldOption"
  | "errors.requiredAdditionalFieldMissing"
  | "errors.additionalFieldValueNotFinite"
  | "errors.additionalFieldValueMalformed"
  | "errors.additionalFieldCountOverCap";

/* ------------------------------------------------------------------ */
/*  Translation tables                                                 */
/* ------------------------------------------------------------------ */

const de: Record<TranslationKey, string> = {
  // form validation
  fieldRequired: "Dieses Feld ist erforderlich.",
  emailRequired: "E-Mail ist erforderlich.",
  dateRequired: "Datum ist erforderlich.",
  selectRequired: "Bitte wählen Sie eine Option.",
  statusRequired: "Bitte wählen Sie einen Status.",
  invalidEmail: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
  invalidUrl: "Bitte geben Sie eine gültige URL ein (http:// oder https://).",
  invalidInteger: "Bitte geben Sie eine gültige Ganzzahl ein.",
  invalidNumber: "Bitte geben Sie eine gültige Zahl ein.",
  minChars: "Mindestens {0} Zeichen erforderlich.",
  maxChars: "Maximal {0} Zeichen erlaubt.",
  minValue: "Mindestwert ist {0}.",
  maxValue: "Maximalwert ist {0}.",
  minOptions: "Bitte wähle mindestens {0} Optionen.",
  maxOptions: "Bitte wähle höchstens {0} Optionen.",
  dateAfter: "Datum muss nach dem {0} liegen.",
  dateBefore: "Datum muss vor dem {0} liegen.",
  checkboxRequired: "Dieses Feld muss bestätigt werden.",
  // IMAGE additional-field upload widget
  imageSelect: "Bild auswählen",
  imageReplace: "Bild ersetzen",
  imageRemove: "Entfernen",
  imageStored: "Bild gespeichert",
  imageUploading: "Wird hochgeladen…",
  imageTooBig: "Das Bild ist zu groß (maximal {0} MB).",
  imageWrongType: "Bitte wählen Sie ein Bild (JPEG, PNG, WebP oder GIF).",
  imageUploadFailed: "Upload fehlgeschlagen. Bitte erneut versuchen.",
  // form submission
  submitSuccess: "Daten erfolgreich übermittelt.",
  submitFailed: "Übermittlung fehlgeschlagen.",
  // labels
  status: "Status",
  yes: "Ja",
  no: "Nein",
  // overview / verification
  verificationPage: "Verifikationsseite",
  checkSubmittedData: "Prüfen Sie Ihre übermittelten Angaben.",
  fields: "Felder",
  companions: "Begleitpersonen",
  companionLabel: "Begleitperson {0}",
  addCompanion: "Begleitperson hinzufügen",
  checkboxes: "Checkboxen",
  overviewBookingLabel: "Buchung",
  // registration
  registrationSuccessful: "Registrierung erfolgreich",
  registrationSubmitted: "Registrierung eingereicht",
  pendingApprovalSubtitle:
    "Ihr Konto wird geprüft. Sie können sich anmelden, sobald es genehmigt wurde.",
  close: "Schließen",
  // misc
  update: "Update",
  noChanges: "Keine Änderungen erkannt.",
  logoutFailed: "Abmeldung fehlgeschlagen.",
  sessionExpired: "Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.",
  slottingLoginRequired: "Bitte melde dich an, um diese Seite zu sehen.",
  errorDuplicateGuest: "Es existiert bereits eine Anmeldung mit diesen Angaben.",
  errorInvalidCredentials: "Benutzername oder Code ist ungültig.",
  errorTooManyAttempts: "Zu viele Fehlversuche. Bitte versuche es später erneut.",
  errorServiceUnavailable:
    "Der Dienst ist vorübergehend nicht erreichbar. Bitte versuche es gleich noch einmal.",
  submitValidationFailed: "Einige Angaben sind ungültig. Bitte prüfe deine Eingaben.",
  errorInvalidFieldType:
    "Einige Angaben haben ein ungültiges Format. Bitte prüfe deine Eingaben.",
  // slotting - booking
  slottingSelectService: "Service auswählen",
  slottingSelectDate: "Datum auswählen",
  slottingAvailableTimes: "Verfügbare Zeiten",
  slottingNoAvailableDates: "Keine verfügbaren Termine.",
  slottingNoAvailableTimes: "Keine verfügbaren Zeiten für dieses Datum.",
  slottingYourId: "Ihre ID",
  slottingEnterGuestId: "Gast-ID eingeben",
  slottingAddCompanions: "Begleitpersonen hinzufügen",
  slottingHideCompanions: "Begleitpersonen ausblenden",
  slottingCompanionId: "Begleitperson {0} ID",
  slottingAddCompanion: "+ Begleitperson hinzufügen",
  slottingRemove: "Entfernen",
  slottingSubmitRequest: "Anfrage senden",
  slottingBookAppointment: "Termin buchen",
  slottingSubmitting: "Wird gesendet...",
  slottingRequestSubmitted: "Anfrage gesendet",
  slottingRequestSubmittedDesc: "Ihre Buchungsanfrage wurde eingereicht. Wir melden uns bei Ihnen.",
  slottingBookingUnavailable: "Terminbuchung nicht verfügbar.",
  // slotting - appointment
  slottingYourAppointment: "Ihr Termin",
  slottingService: "Service",
  slottingLocation: "Ort",
  slottingDateTime: "Datum & Uhrzeit",
  slottingOffering: "Angebot",
  slottingDownloadCalendar: "Kalenderdatei herunterladen",
  slottingCancelledMessage: "Dieser Termin wurde storniert.",
  slottingStatusMessage: "Dieser Termin ist {0}.",
  slottingInProgress: "derzeit im Gange",
  slottingTentative: "Vorläufig",
  slottingConfirmed: "Bestätigt",
  slottingAppointmentStatus: "Status",
  slottingCancelButton: "Termin stornieren",
  slottingCancelConfirm: "Möchten Sie diesen Termin wirklich stornieren?",
  slottingCancelling: "Wird storniert...",
  slottingYesCancel: "Ja, stornieren",
  slottingKeepAppointment: "Termin behalten",
  slottingCancelledSuccess: "Ihr Termin wurde storniert.",
  slottingCancellationFailed: "Stornierung fehlgeschlagen.",
  slottingDeadlinePassed: "Die Stornierungsfrist für diesen Termin ist abgelaufen.",
  slottingAppointmentNotFound: "Termin nicht gefunden.",
  slottingRescheduled: "Dieser Termin wurde verschoben.",
  slottingViewNewAppointment: "Neuen Termin anzeigen",
  // slotting - waitlist
  slottingWaitlistStatus: "Wartelistenstatus",
  slottingWaitlistNotFound: "Wartelisteneintrag nicht gefunden.",
  slottingWaitlistPosition: "Ihre Position",
  slottingWaitlistPromoted: "Ihr Platz wurde bestätigt!",
  slottingViewAppointment: "Termin anzeigen",
  slottingLeaveWaitlist: "Warteliste verlassen",
  slottingLeaving: "Wird verlassen...",
  slottingLeftWaitlist: "Sie haben die Warteliste verlassen.",
  slottingWaitlistInactive: "Dieser Wartelisteneintrag ist nicht mehr aktiv.",
  slottingWaitlistStatusWaiting: "Wartend",
  slottingWaitlistStatusPromoted: "Bestätigt",
  slottingWaitlistStatusExpired: "Abgelaufen",
  slottingWaitlistStatusCancelled: "Storniert",
  // slotting - spots
  slottingSpotsLeft: "{0} verfügbar",
  // slotting - availability
  slottingAvailable: "Verfügbar",
  slottingFull: "Ausgebucht",
  slottingNone: "Keine",
  // slotting - request-based
  slottingSelectTreatments: "Behandlungen auswählen",
  slottingYourAvailability: "Ihre Verfügbarkeit",
  slottingAddWindow: "Zeitfenster hinzufügen",
  slottingDateLabel: "Datum",
  slottingStartTime: "Von",
  slottingEndTime: "Bis",
  slottingTreatmentsSelected: "{0} Behandlungen",
  slottingWindowsAdded: "{0} Zeitfenster",
  slottingRequestSummary: "{0} Behandlungen · {1} Zeitfenster",
  slottingNoRequestConfigured: "Keine Anfrage konfiguriert",
  // companion modal
  companionEdit: "Bearbeiten",
  companionSave: "Speichern",
  companionCancel: "Abbrechen",
  companionNoName: "Keine Angaben",
  // booking modal
  bookingEdit: "Bearbeiten",
  bookingBook: "Termin buchen",
  bookingNoAppointment: "Kein Termin gebucht",
  bookingConfirm: "Bestätigen",
  bookingAccessDenied: "Terminbuchung ist derzeit nicht freigeschaltet.",
  bookingTreatmentsUnavailable: "Behandlungen werden vom Veranstalter zugewiesen.",
  // booking validation
  bookingWindowDateRequired: "Bitte füllen Sie alle Felder des Zeitfensters aus.",
  bookingWindowEndBeforeStart: "Die Endzeit muss nach der Startzeit liegen.",
  bookingWindowInPast: "Zeitfenster können nicht in der Vergangenheit liegen.",
  bookingWindowOutsideRange: "Das Datum liegt außerhalb des verfügbaren Zeitraums.",
  bookingTreatmentSelectionRequired: "Bitte wählen Sie mindestens eine Behandlung aus.",
  bookingSelectionRequired: "Bitte wählen Sie einen Termin aus.",
  bookingSubmissionFailed: "Buchung fehlgeschlagen. Ihre Registrierung war erfolgreich.",
  bookingSelectDate: "Datum wählen",
  bookingSelectTime: "Uhrzeit wählen",
  bookingDetails: "Buchungsdetails",
  bookingSelectTreatmentRequired: "Bitte wählen Sie eine Behandlung aus.",
  bookingSelectDateRequired: "Bitte wählen Sie ein Datum aus.",
  bookingSelectSlotRequired: "Bitte wählen Sie einen Zeitslot aus.",
  // status badges
  slottingCancelled: "Storniert",
  slottingCheckedIn: "Eingecheckt",
  // error fallbacks
  slottingFailedToLoad: "Laden fehlgeschlagen.",
  slottingFailedToLeaveWaitlist: "Warteliste verlassen fehlgeschlagen.",
  slottingBookingFailed: "Buchung fehlgeschlagen.",
  slottingNoAvailabilityData: "Keine Verfügbarkeitsdaten.",
  // registration/login rules
  registrationClosed: "Die Registrierung ist derzeit geschlossen.",
  loginClosed: "Die Anmeldung ist derzeit nicht möglich.",
  waitlistTitle: "Auf der Warteliste",
  waitlistSubtitle: "Sie wurden auf die Warteliste gesetzt. Wir benachrichtigen Sie, sobald ein Platz frei wird.",
  waitlistPosition: "Ihre Position: {0}",
  // capacity refusals on submit
  registrationSegmentFull:
    "Für diese Anmeldung sind derzeit keine Plätze mehr verfügbar.",
  companionLimitReached:
    "Es können nicht so viele Begleitpersonen angemeldet werden. Bitte reduzieren Sie die Anzahl.",
  // option capacity
  optionCapacityFull:
    "„{0}“ ist nicht mehr verfügbar — bitte wählen Sie eine andere Option.",
  optionCapacityFullGeneric:
    "Die gewählte Option ist nicht mehr verfügbar — bitte wählen Sie eine andere Option.",
  // session-selection programme step
  sessionStepLabel: "Programm",
  sessionSeatsLeft: "Noch {0} verfügbar",
  sessionSoldOut: "Ausgebucht",
  sessionSelectedCount: "{0} ausgewählt",
  sessionMinHint: "mindestens {0}",
  sessionMaxHint: "höchstens {0}",
  sessionMinError: "Bitte wählen Sie mindestens {0} Programmpunkte aus.",
  sessionMaxError: "Bitte wählen Sie höchstens {0} Programmpunkte aus.",
  sessionSoldOutSelected:
    "Ein ausgewählter Programmpunkt ist ausgebucht — bitte wählen Sie einen anderen.",
  sessionFullError:
    "Einige gewählte Programmpunkte sind inzwischen ausgebucht — bitte passen Sie Ihre Auswahl an.",
  sessionsUnavailable: "Programm derzeit nicht verfügbar.",
  sessionsEmpty: "Keine Programmpunkte verfügbar.",
  sessionShowMore: "Mehr anzeigen",
  sessionShowLess: "Weniger anzeigen",
  // OFFERING slot booking inside the programme step
  offeringChooseSlot: "Zeitfenster wählen",
  offeringChangeSlot: "Zeitfenster ändern",
  offeringHideSlots: "Zeitfenster ausblenden",
  offeringOnRequest:
    "Buchung auf Anfrage — das Team teilt Ihnen einen Termin zu.",
  offeringCurrentBooking: "Ihr Termin",
  offeringRemoveSelection: "Auswahl entfernen",
  offeringSlotsUnavailable: "Zeitfenster derzeit nicht verfügbar.",
  offeringRetry: "Erneut versuchen",
  offeringConflictError:
    "Das gewählte Zeitfenster ist nicht mehr verfügbar — bitte wählen Sie ein neues.",
  offeringNotBookableError:
    "Dieses Angebot kann nicht direkt gebucht werden — es wird auf Anfrage vergeben.",
  offeringChooseTreatment: "Terminart wählen",
  offeringPickSlotHint:
    "Bitte wählen Sie noch ein Zeitfenster — ohne Zeitfenster wird kein Termin gebucht.",
  offeringMultipleAppointments:
    "Mehrere Termine in diesem Block — Änderungen bitte am Empfang.",
  offeringMultipleAppointmentsError:
    "Für diesen Programmpunkt bestehen mehrere Termine — Änderungen sind nur am Empfang möglich.",
  offeringNoTypes: "Derzeit keine buchbaren Terminarten.",
  // additionalField validation
  "errors.additionalFieldsOverCap": "Zu viele zusätzliche Felder (maximal 20).",
  "errors.invalidAdditionalFieldKey": "Der Feldname darf nur Buchstaben, Zahlen, Bindestriche oder Unterstriche enthalten.",
  "errors.additionalFieldKeyCollision": "Ein Feld mit diesem Namen existiert bereits.",
  "errors.reservedAdditionalFieldKey": "Dieser Feldname ist reserviert.",
  "errors.invalidAdditionalFieldConstraints": "Die Feldbeschränkungen sind ungültig.",
  "errors.additionalFieldsValidationFailed": "Einige Felder haben die Prüfung nicht bestanden.",
  "errors.unknownAdditionalFieldKey": "Dieses Feld ist für diese Veranstaltung nicht konfiguriert.",
  "errors.invalidAdditionalFieldValueType": "Der Wert hat den falschen Typ für dieses Feld.",
  "errors.additionalFieldValueOutOfRange": "Der Wert liegt außerhalb des zulässigen Bereichs.",
  "errors.invalidAdditionalFieldOption": "Diese Option ist für dieses Feld nicht zulässig.",
  "errors.requiredAdditionalFieldMissing": "Dieses Feld ist erforderlich.",
  "errors.additionalFieldValueNotFinite": "Der numerische Wert muss endlich sein.",
  "errors.additionalFieldValueMalformed": "Der Wert hat kein gültiges Format.",
  "errors.additionalFieldCountOverCap": "Zu viele Formularfelder für diese Veranstaltung (maximal 50).",
};

const en: Record<TranslationKey, string> = {
  // form validation
  fieldRequired: "This field is required.",
  emailRequired: "Email is required.",
  dateRequired: "Date is required.",
  selectRequired: "Please select an option.",
  statusRequired: "Please select a status.",
  invalidEmail: "Please enter a valid email address.",
  invalidUrl: "Please enter a valid URL (http:// or https://).",
  invalidInteger: "Please enter a valid integer.",
  invalidNumber: "Please enter a valid number.",
  minChars: "Minimum {0} characters required.",
  maxChars: "Maximum {0} characters allowed.",
  minValue: "Minimum value is {0}.",
  maxValue: "Maximum value is {0}.",
  minOptions: "Please select at least {0} options.",
  maxOptions: "Please select at most {0} options.",
  dateAfter: "Date must be after {0}.",
  dateBefore: "Date must be before {0}.",
  checkboxRequired: "This checkbox is required.",
  // IMAGE additional-field upload widget
  imageSelect: "Choose image",
  imageReplace: "Replace image",
  imageRemove: "Remove",
  imageStored: "Image saved",
  imageUploading: "Uploading…",
  imageTooBig: "The image is too large (maximum {0} MB).",
  imageWrongType: "Please choose an image (JPEG, PNG, WebP or GIF).",
  imageUploadFailed: "Upload failed. Please try again.",
  // form submission
  submitSuccess: "Data submitted successfully.",
  submitFailed: "Submission failed.",
  // labels
  status: "Status",
  yes: "Yes",
  no: "No",
  // overview / verification
  verificationPage: "Verification page",
  checkSubmittedData: "Please review your submitted data.",
  fields: "Fields",
  companions: "Companions",
  companionLabel: "Companion {0}",
  addCompanion: "Add companion",
  checkboxes: "Checkboxes",
  overviewBookingLabel: "Booking",
  // registration
  registrationSuccessful: "Registration Successful",
  registrationSubmitted: "Registration Submitted",
  pendingApprovalSubtitle:
    "Your account is pending approval. You will be able to log in once approved.",
  close: "Close",
  // misc
  update: "Update",
  noChanges: "No changes detected.",
  logoutFailed: "Logout failed.",
  sessionExpired: "Your session has expired. Please log in again.",
  slottingLoginRequired: "Please log in to view this page.",
  errorDuplicateGuest: "A registration with these details already exists.",
  errorInvalidCredentials: "Username or code is invalid.",
  errorTooManyAttempts: "Too many failed attempts. Please try again later.",
  errorServiceUnavailable:
    "The service is temporarily unavailable. Please try again in a moment.",
  submitValidationFailed: "Some entries are invalid. Please check your input.",
  errorInvalidFieldType:
    "Some entries have an invalid format. Please check your input.",
  // slotting - booking
  slottingSelectService: "Select a service",
  slottingSelectDate: "Select a date",
  slottingAvailableTimes: "Available times",
  slottingNoAvailableDates: "No available dates.",
  slottingNoAvailableTimes: "No available times for this date.",
  slottingYourId: "Your ID",
  slottingEnterGuestId: "Enter your guest ID",
  slottingAddCompanions: "Add companions",
  slottingHideCompanions: "Hide companions",
  slottingCompanionId: "Companion {0} ID",
  slottingAddCompanion: "+ Add companion",
  slottingRemove: "Remove",
  slottingSubmitRequest: "Submit Request",
  slottingBookAppointment: "Book Appointment",
  slottingSubmitting: "Submitting...",
  slottingRequestSubmitted: "Request Submitted",
  slottingRequestSubmittedDesc: "Your booking request has been submitted. We'll be in touch.",
  slottingBookingUnavailable: "Booking not available.",
  // slotting - appointment
  slottingYourAppointment: "Your Appointment",
  slottingService: "Service",
  slottingLocation: "Location",
  slottingDateTime: "Date & Time",
  slottingOffering: "Offering",
  slottingDownloadCalendar: "Download Calendar",
  slottingCancelledMessage: "This appointment has been cancelled.",
  slottingStatusMessage: "This appointment is {0}.",
  slottingInProgress: "currently in progress",
  slottingTentative: "Tentative",
  slottingConfirmed: "Confirmed",
  slottingAppointmentStatus: "Status",
  slottingCancelButton: "Cancel Appointment",
  slottingCancelConfirm: "Are you sure you want to cancel this appointment?",
  slottingCancelling: "Cancelling...",
  slottingYesCancel: "Yes, Cancel",
  slottingKeepAppointment: "Keep Appointment",
  slottingCancelledSuccess: "Your appointment has been cancelled.",
  slottingCancellationFailed: "Cancellation failed.",
  slottingDeadlinePassed: "The cancellation deadline has passed for this appointment.",
  slottingAppointmentNotFound: "Appointment not found.",
  slottingRescheduled: "This appointment has been rescheduled.",
  slottingViewNewAppointment: "View New Appointment",
  // slotting - waitlist
  slottingWaitlistStatus: "Waitlist Status",
  slottingWaitlistNotFound: "Waitlist entry not found.",
  slottingWaitlistPosition: "Your Position",
  slottingWaitlistPromoted: "Great news! Your spot has been confirmed.",
  slottingViewAppointment: "View Your Appointment",
  slottingLeaveWaitlist: "Leave Waitlist",
  slottingLeaving: "Leaving...",
  slottingLeftWaitlist: "You have left the waitlist.",
  slottingWaitlistInactive: "This waitlist entry is no longer active.",
  slottingWaitlistStatusWaiting: "Waiting",
  slottingWaitlistStatusPromoted: "Promoted",
  slottingWaitlistStatusExpired: "Expired",
  slottingWaitlistStatusCancelled: "Cancelled",
  // slotting - spots
  slottingSpotsLeft: "{0} left",
  // slotting - availability
  slottingAvailable: "Available",
  slottingFull: "Full",
  slottingNone: "None",
  // slotting - request-based
  slottingSelectTreatments: "Select treatments",
  slottingYourAvailability: "Your availability",
  slottingAddWindow: "Add time window",
  slottingDateLabel: "Date",
  slottingStartTime: "From",
  slottingEndTime: "To",
  slottingTreatmentsSelected: "{0} treatments",
  slottingWindowsAdded: "{0} time windows",
  slottingRequestSummary: "{0} treatments · {1} time windows",
  slottingNoRequestConfigured: "No request configured",
  // companion modal
  companionEdit: "Edit",
  companionSave: "Save",
  companionCancel: "Cancel",
  companionNoName: "No details",
  // booking modal
  bookingEdit: "Edit",
  bookingBook: "Book appointment",
  bookingNoAppointment: "No appointment booked",
  bookingConfirm: "Confirm",
  bookingAccessDenied: "Booking is currently not enabled.",
  bookingTreatmentsUnavailable: "Treatments will be confirmed by the organizer.",
  // booking validation
  bookingWindowDateRequired: "Please complete all fields in the time window.",
  bookingWindowEndBeforeStart: "End time must be after start time.",
  bookingWindowInPast: "Time windows cannot be in the past.",
  bookingWindowOutsideRange: "Date is outside the available range.",
  bookingTreatmentSelectionRequired: "Please select at least one treatment.",
  bookingSelectionRequired: "Please select an appointment.",
  bookingSubmissionFailed: "Booking failed. Your registration was successful.",
  bookingSelectDate: "Select date",
  bookingSelectTime: "Select time",
  bookingDetails: "Booking details",
  bookingSelectTreatmentRequired: "Please select a treatment.",
  bookingSelectDateRequired: "Please select a date.",
  bookingSelectSlotRequired: "Please select a time slot.",
  // status badges
  slottingCancelled: "Cancelled",
  slottingCheckedIn: "Checked In",
  // error fallbacks
  slottingFailedToLoad: "Failed to load.",
  slottingFailedToLeaveWaitlist: "Failed to leave waitlist.",
  slottingBookingFailed: "Booking failed.",
  slottingNoAvailabilityData: "No availability data.",
  // registration/login rules
  registrationClosed: "Registration is currently closed.",
  loginClosed: "Login is currently unavailable.",
  waitlistTitle: "On the Waitlist",
  waitlistSubtitle: "You have been added to the waitlist. We will notify you when a spot becomes available.",
  waitlistPosition: "Your position: {0}",
  // capacity refusals on submit
  registrationSegmentFull:
    "There are currently no places left for this registration.",
  companionLimitReached:
    "That many companions cannot be registered. Please reduce the number.",
  // option capacity
  optionCapacityFull:
    "“{0}” is no longer available — please pick another option.",
  optionCapacityFullGeneric:
    "Your selected option is no longer available — please pick another option.",
  // session-selection programme step
  sessionStepLabel: "Programme",
  sessionSeatsLeft: "{0} seats left",
  sessionSoldOut: "Sold out",
  sessionSelectedCount: "{0} selected",
  sessionMinHint: "at least {0}",
  sessionMaxHint: "at most {0}",
  sessionMinError: "Please select at least {0} sessions.",
  sessionMaxError: "Please select at most {0} sessions.",
  sessionSoldOutSelected:
    "A selected session is sold out — please pick a different one.",
  sessionFullError:
    "Some selected sessions just sold out — please adjust your selection.",
  sessionsUnavailable: "Programme currently unavailable.",
  sessionsEmpty: "No sessions available.",
  sessionShowMore: "Show more",
  sessionShowLess: "Show less",
  // OFFERING slot booking inside the programme step
  offeringChooseSlot: "Choose a time slot",
  offeringChangeSlot: "Change time slot",
  offeringHideSlots: "Hide time slots",
  offeringOnRequest:
    "Booking on request — the team will assign your appointment.",
  offeringCurrentBooking: "Your appointment",
  offeringRemoveSelection: "Remove selection",
  offeringSlotsUnavailable: "Time slots currently unavailable.",
  offeringRetry: "Retry",
  offeringConflictError:
    "The selected time slot is no longer available — please pick a new one.",
  offeringNotBookableError:
    "This offering cannot be booked directly — it is assigned on request.",
  offeringChooseTreatment: "Choose a service",
  offeringPickSlotHint:
    "Please also pick a time slot — no appointment is booked without one.",
  offeringMultipleAppointments:
    "Multiple appointments in this block — please see the reception desk for changes.",
  offeringMultipleAppointmentsError:
    "This block has multiple appointments — changes are only possible at the reception desk.",
  offeringNoTypes: "No bookable appointment types at the moment.",
  // additionalField validation (mirrors backend error codes)
  "errors.additionalFieldsOverCap": "Too many additional fields (maximum 20).",
  "errors.invalidAdditionalFieldKey": "Field name must only contain letters, numbers, hyphens, or underscores.",
  "errors.additionalFieldKeyCollision": "A field with this name already exists.",
  "errors.reservedAdditionalFieldKey": "This field name is reserved.",
  "errors.invalidAdditionalFieldConstraints": "Field constraints are invalid.",
  "errors.additionalFieldsValidationFailed": "Some fields failed validation.",
  "errors.unknownAdditionalFieldKey": "This field is not configured for this event.",
  "errors.invalidAdditionalFieldValueType": "The value has the wrong type for this field.",
  "errors.additionalFieldValueOutOfRange": "The value is outside the allowed range.",
  "errors.invalidAdditionalFieldOption": "This option is not allowed for this field.",
  "errors.requiredAdditionalFieldMissing": "This field is required.",
  "errors.additionalFieldValueNotFinite": "Numeric value must be finite.",
  "errors.additionalFieldValueMalformed": "Value is not a valid format.",
  "errors.additionalFieldCountOverCap": "Too many form fields on this event (maximum 50).",
};

const tables: Record<Locale, Record<TranslationKey, string>> = { de, en };

/* ------------------------------------------------------------------ */
/*  Public API                                                         */
/* ------------------------------------------------------------------ */

/**
 * Translate a key, optionally interpolating positional arguments.
 *
 * Placeholders use the pattern `{0}`, `{1}`, etc.
 *
 * @example
 *   t("minChars", "5")   // "Mindestens 5 Zeichen erforderlich."
 *   t("fieldRequired")   // "Dieses Feld ist erforderlich."
 */
export function t(key: TranslationKey, ...args: string[]): string {
  const table = tables[currentLocale] ?? tables[DEFAULT_LOCALE];
  let text = table[key] ?? tables[DEFAULT_LOCALE][key] ?? key;
  for (let i = 0; i < args.length; i++) {
    text = text.replace(`{${String(i)}}`, args[i] as string);
  }
  return text;
}
